import {
    Box,
    Button,
    Grid2,
    Icon,
    InputBase,
    Paper,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { MessageSeverityEnum } from '../../../_common/enum/messageSeverity.enum';
import { IDropdownData } from '../../../_common/interface/dropdown/iDropdownData.interface';
import { toastr } from '../../../_components/toastr/toastr';
import { handleErrorResponse } from '../../../_helpers/handleResponse.helper';
import { auctionMessageService } from '../../../_services/auctionMessage.service';
import AuctionNotificationType from './auction-notificationType';
import AuctionUsersDropdown from './auction-usersDropdown';
import AuctionDefaultMessageDropdown from './auction-defaultMessageDropdown';

export function AuctionChat(params: { auctionId: number; catalogueId: number }) {
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ message: "", messageSeverityId: null, catalogueUserId: null });

    const [catalogueUser, setAuctionUser] = useState<IDropdownData | null>(null);
    const [messageSeverityId, setMessageSeverityId] = useState<number | null>(null);

    function handleChange(event: any) {
        setMessage((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    useEffect(() => {
        if (!!params.catalogueId)
            setLoading(false);
    }, [params.catalogueId]);

    function create() {
        if (!params.catalogueId) return;

        setLoading(true);

        return auctionMessageService.post(params.auctionId, params.catalogueId, message.message, messageSeverityId, catalogueUser?.id)
            .then(
                () => {
                    setMessage({ message: "", messageSeverityId: null, catalogueUserId: null });
                    setAuctionUser(null);
                    setMessageSeverityId(null);
                    toastr.success("Message posted");
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <Paper
            elevation={3}
            sx={{
                p: 1.5,
                backgroundColor: "background.paper",
            }}
        >
            <ValidatorForm
                onSubmit={(e) => {
                    e.preventDefault();
                    if (message.message?.trim()) create();
                }}
                noValidate
            >
                <Grid2 container spacing={1.25}>
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Admin Chat
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Send a broadcast or targeted notification to catalogue users.
                        </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                        <Paper
                            variant="outlined"
                            sx={{
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1.5,
                            }}
                        >
                            <InputBase
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={5}
                                placeholder="Type auction notification..."
                                inputProps={{ 'aria-label': 'type notification' }}
                                name="message"
                                value={message.message}
                                onChange={handleChange}
                            />
                        </Paper>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <AuctionDefaultMessageDropdown
                            onChange={(d: any) => {
                                if (!d) return;
                                setMessage((m: any) => ({ ...m, message: d.name, messageSeverityId: d.messageSeverityId }));
                                setMessageSeverityId(d.messageSeverityId);
                            }}
                            buttonStyle={{ height: "40px", width: "100%" }}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 3 }}>
                        <AuctionNotificationType
                            messageSeverityId={messageSeverityId}
                            setMessageSeverityId={setMessageSeverityId}
                            onChange={(severityId) => {
                                setMessage((m: any) => ({ ...m, messageSeverityId: severityId }));
                            }}
                            buttonStyle={{ height: "40px", width: "100%" }}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 3 }}>
                        <AuctionUsersDropdown
                            style={{ height: "40px", width: "100%" }}
                            catalogueUser={catalogueUser}
                            setAuctionUser={setAuctionUser}
                            catalogueId={params.catalogueId}
                            onChange={(d) => {
                                setMessage((m: any) => ({ ...m, catalogueUserId: d }));
                            }}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 2 }}>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="success"
                            endIcon={<Icon>send</Icon>}
                            disabled={loading || !message.message?.trim()}
                            sx={{ height: "40px", fontWeight: 700 }}
                        >
                            SEND
                        </Button>
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                        <Box sx={{ px: 0.25 }}>
                            <Typography variant="caption" color="text.secondary">
                                Target: {catalogueUser?.name ?? "All users"}{messageSeverityId ? ` • Severity: ${MessageSeverityEnum[messageSeverityId]}` : " • Severity: Standard"}
                            </Typography>
                        </Box>
                    </Grid2>
                </Grid2>
            </ValidatorForm>
        </Paper>
    );
}
